/**
 * A session's timeline: the lobby opens, play starts, the end lobby, the room
 * closes — each at its local time, and dated where it falls on another day
 * than the start (sessionTimeline).
 *
 * The sessions list's card and the session form's preview both draw it. Each
 * had written the row out, with a rule of its own for it, and the card dated
 * its stops against the start's UTC day: west of UTC an evening session's
 * every stop carried a date, its start's own included, while the end lobby
 * after midnight carried none.
 */

import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Pill, type PillTone } from '../../components/pill';
import { sessionTimeline } from '../../api/admin/sessions/helpers';
import type { TimelinePhase } from '../../api/admin/sessions/types';
import styles from './session-timeline.module.css';

/** How each stop reads: its words, its mark and its tone. */
const STOP_LOOK: Record<TimelinePhase, { key: string; mark: string; tone: PillTone }> = {
  startLobby: { key: 'admin.sessions.phaseStartLobby', mark: '🚪', tone: 'info' },
  playing: { key: 'admin.sessions.phasePlaying', mark: '🎮', tone: 'success' },
  endLobby: { key: 'admin.sessions.phaseEndLobby', mark: '🏁', tone: 'warning' },
  closes: { key: 'admin.sessions.phaseCloses', mark: '🔒', tone: 'neutral' },
};

interface SessionTimelineProps {
  /** The session's start, in epoch seconds. */
  startUtc: number;
  /** How long it runs, in minutes (sessionLengthMinutes). */
  lengthMinutes: number;
  /** A script-driven session's timing may shift with its script, which the row says first. */
  scriptDriven: boolean;
  /** Where the row sits — the card's rule and padding. */
  className?: string;
}

export function SessionTimeline({ startUtc, lengthMinutes, scriptDriven, className }: SessionTimelineProps) {
  const { t } = useTranslation();
  return (
    <div className={className ? `${styles.timeline} ${className}` : styles.timeline}>
      {scriptDriven && <span className={styles.note}>{t('admin.sessions.sessionScriptDrivenTimeline')}</span>}
      {sessionTimeline(startUtc, lengthMinutes).map((stop, i) => {
        const look = STOP_LOOK[stop.phase];
        return (
          <Fragment key={stop.phase}>
            {i > 0 && <span className={styles.arrow}>→</span>}
            <Pill solid tone={look.tone}>
              {look.mark} {t(look.key)} <strong>{stop.time}{stop.otherDay && ` (${stop.otherDay})`}</strong>
            </Pill>
          </Fragment>
        );
      })}
    </div>
  );
}
